import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Handshake, Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { StackedList, Pagination } from "@/components/shared/StackedList";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

export default function Suppliers() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQRaw] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const setQ = (v) => { setQRaw(v); setPage(1); }; // a new search always starts at page 1
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", region: "", status: "PENDING" });
  const navigate = useNavigate();

  const load = () => {
    const params = { page, page_size: PAGE_SIZE, ...(q ? { q } : {}) };
    api.get("/directory/suppliers", { params }).then((r) => setData(r.data)).catch(() => setError(true));
  };
  useEffect(() => { load(); }, [q, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    try {
      await api.post("/directory/suppliers", form);
      toast.success("Supplier created");
      setOpen(false);
      setForm({ name: "", region: "", status: "PENDING" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create supplier"); }
  };

  if (error) return <ErrorState testId="suppliers-error" />;

  return (
    <div data-testid="suppliers-page" className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.01em] text-jigsaw-navy">Suppliers</h1>
          <p className="text-sm text-jigsaw-muted">Hotels, apartments and accommodation suppliers approved to work with Jigsaw.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-supplier-button" className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark"><Plus className="mr-1 h-4 w-4" /> New Supplier</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New supplier</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="supplier-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Region</Label><Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="supplier-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="PENDING">Pending onboarding</SelectItem><SelectItem value="APPROVED">Approved</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button data-testid="supplier-save-button" onClick={submit} disabled={!form.name}>Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Input data-testid="suppliers-search-input" placeholder="Search suppliers..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />

      {!data ? (
        <LoadingState testId="suppliers-loading" />
      ) : data.items.length === 0 ? (
        <EmptyState testId="suppliers-empty" icon={Handshake} title="No suppliers found" />
      ) : (
        <div>
          <StackedList testId="suppliers-stacked" items={data.items} title={(s) => s.name} status={(s) => <StatusBadge status={s.status} />} onOpen={(s) => navigate(`/control-centre/suppliers/${s.id}`)} openLabel="Open supplier"
            fields={[{ label: "Region", value: (s) => s.region, full: true }]} />
          <div className="hidden sm:block">
          <Table data-testid="suppliers-table">
            <TableHeader>
              <TableRow><TableHead>Name</TableHead><TableHead>Region</TableHead><TableHead>Status</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((s) => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`} className="cursor-pointer hover:bg-jigsaw-bg" onClick={() => navigate(`/control-centre/suppliers/${s.id}`)}>
                  <TableCell className="font-medium text-jigsaw-navy">{s.name}</TableCell>
                  <TableCell>{s.region || "—"}</TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <Pagination testId="suppliers-pagination" page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}
